import { useCallback } from 'react';
import { useUser, useUserState } from '../query/UserProvider';
import { useUpdateUserStateItem } from '../query/user-state/useUpdateUserStateItem';
import { useDeleteUserStateItem } from '../query/user-state/useDeleteUserStateItem';
import { normalizeUserStateVersion } from '../utils/user-state/normalize-user-state-version';
import type { ApplicationKitState, UserStateApplicationKitKey, UserStateEntry } from '../types/user-state';

export function applicationKitItemKey(jobId: string): UserStateApplicationKitKey {
  return `applicationKit#${jobId}`;
}

/**
 * Read/write/delete access to the `applicationKit#{jobId}` userState bucket — the persisted
 * tailored CV + cover letter output for a job. Kits are account-only (no anonymous/local-only
 * fallback like saved jobs get): they contain real generated content tied to a paid LLM call,
 * so there's nothing meaningful to keep client-side-only for a signed-out user.
 *
 * TEC-404: uses the TanStack Query layer (query/user-state/useUpdateUserStateItem.ts /
 * useDeleteUserStateItem.ts) exclusively — the Redux userState.entries dual-write (setEntry/
 * removeEntry) was removed in Phase C once every `state.userState` reader had migrated. No
 * guest-mode branching needed here beyond what the mutation hooks already do internally:
 * saveApplicationKit still gates on isAuthenticated up front (unlike saved jobs/follows, there is
 * no local-only kit to create for a guest), matching the file-level doc comment above.
 */
export function useApplicationKitActions() {
  const { data: userStateEntries } = useUserState();
  const { isAuthenticated } = useUser();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();

  const getApplicationKit = useCallback(
    (jobId: string): ApplicationKitState | null => {
      const entry = (userStateEntries ?? {})[applicationKitItemKey(jobId)] as UserStateEntry | undefined;
      const data = entry?.data as ApplicationKitState | undefined;
      return data && Object.keys(data).length > 0 ? data : null;
    },
    [userStateEntries]
  );

  const saveApplicationKit = useCallback(
    async (jobId: string, kit: ApplicationKitState): Promise<boolean> => {
      if (!isAuthenticated) return false;

      const itemKey = applicationKitItemKey(jobId);
      const currentEntry = (userStateEntries ?? {})[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await updateUserStateItem.mutateAsync({ itemKey, data: kit, version });
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[application-kit] Failed to persist kit', error);
        }
        return false;
      }
    },
    [updateUserStateItem, userStateEntries, isAuthenticated]
  );

  const deleteApplicationKit = useCallback(
    async (jobId: string): Promise<boolean> => {
      const itemKey = applicationKitItemKey(jobId);
      const currentEntry = (userStateEntries ?? {})[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);

      try {
        await deleteUserStateItem.mutateAsync({ itemKey, version: version > 0 ? version : undefined });
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[application-kit] Failed to delete kit', error);
        }
        return false;
      }
    },
    [deleteUserStateItem, userStateEntries]
  );

  return { getApplicationKit, saveApplicationKit, deleteApplicationKit };
}
